import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { ILoggingAction } from "@faire/web-api--source/indigofair/data/ILoggingAction";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import React, { useMemo } from "react";

import { SDUIOnViewHandler } from "@faire/retailer/components/SDUI/__internal__/SDUIOnViewHandler/SDUIOnViewHandler";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
} from "@faire/retailer/components/SDUI/__internal__/types";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { getCssFromStyle } from "@faire/retailer/components/SDUI/ComponentRenderers/Tile/__internal__/getCssFromTileStyle";
import { SDUITileRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Tile/SDUITileRenderer";

export const SDUIGridRow: React.FC<{
  tiles: ITile[];
  loggingData: ComponentLoggingEventParams;
  className?: string;
  componentStyle?: React.CSSProperties;
  containerRef?: React.MutableRefObject<HTMLDivElement | null>;
  logTileBatchView?: IGridComponent.ILogTileBatchView;
  shouldSetIsScrollingTopToFalseOnView?: boolean;
}> = ({
  tiles,
  loggingData,
  className,
  componentStyle,
  containerRef,
  logTileBatchView,
  shouldSetIsScrollingTopToFalseOnView,
}) => {
  const actionLoggingParams = useMemo(() => {
    const actionContent = tiles
      .map((tile) => tile.logging_data?.instance_identifier)
      .join(" ");
    const actionPositions = tiles
      .map((tile) => tile.logging_data?.tile_position)
      .join(" ");

    return ActionLoggingEventParams.build({
      ...loggingData,
      action_content: actionContent,
      action_positions: actionPositions,
    });
  }, [loggingData, tiles]);

  const onViewActions = useMemo(() => {
    if (!logTileBatchView) {
      return [];
    }

    return [
      IAction.build({
        log_event: ILoggingAction.build({
          event_name: logTileBatchView.event_name,
          parameters: logTileBatchView.parameters.concat(
            actionLoggingParams.action_content ?? []
          ),
        }),
      }),
    ];
  }, [actionLoggingParams.action_content, logTileBatchView]);

  return (
    <SDUIOnViewHandler
      loggingData={actionLoggingParams}
      onViewActions={onViewActions}
      className={className}
      componentStyle={componentStyle}
      logSDUIViewEvent
      logLegacyViewEvent
      shouldSetIsScrollingTopToFalseOnView={
        shouldSetIsScrollingTopToFalseOnView
      }
      ref={containerRef}
    >
      {tiles.map((tile, index) => (
        <SDUIOnViewHandler
          onViewActions={tile.on_view}
          loggingData={ActionLoggingEventParams.build({
            ...loggingData,
            action_instance_identifier:
              tile.logging_data?.instance_identifier ?? "",
            action_positions: tile.logging_data?.tile_position?.toString(),
          })}
          componentStyle={{
            height: "100%",
            ...getCssFromStyle(tile.style),
          }}
          key={tile.logging_data?.instance_identifier ?? index}
        >
          <SDUITileRenderer
            key={tile.logging_data?.instance_identifier ?? index}
            tileElement={tile}
            loggingData={loggingData}
          />
        </SDUIOnViewHandler>
      ))}
    </SDUIOnViewHandler>
  );
};
